import React, { useState } from 'react';
import { AlertCircle, CheckCircle, Mail, Send } from 'lucide-react';
import { sendContactForm, ContactFormData } from '../lib/email';
import { trackEvent } from '../lib/analytics';

const Contact: React.FC = () => {
  const [formData, setFormData] = useState<ContactFormData>({ name: '', email: '', company: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const handleChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [event.target.name]: event.target.value });
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');
    setErrorMessage('');

    try {
      const result = await sendContactForm(formData);
      if (!result.success) throw new Error(result.error || 'No se pudo enviar el mensaje');
      setSubmitStatus('success');
      trackEvent('contact_submit', { source: 'contact_form' });
      setFormData({ name: '', email: '', company: '', message: '' });
    } catch (error) {
      setSubmitStatus('error');
      setErrorMessage(error instanceof Error ? error.message : 'No se pudo enviar el mensaje');
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputClass = 'w-full rounded-md border border-[#b8c5bc] bg-white px-4 py-3 text-[#07110d] outline-none transition-colors placeholder:text-[#8a988f] focus:border-[#15783a]';

  return (
    <section id="contact" className="border-b border-[#c9d2cc] bg-white py-14 md:py-16">
      <div className="mx-auto grid max-w-[1240px] gap-10 px-5 sm:px-8 lg:grid-cols-[0.75fr_1.25fr] lg:px-10">
        <div>
          <h2 className="max-w-[10ch] text-4xl font-semibold leading-[1] tracking-[-0.045em] text-[#07110d] md:text-5xl">Contacto.</h2>
          <p className="mt-6 max-w-[36ch] text-base leading-7 text-[#526158]">Cuéntanos qué necesitas y te responderemos por correo.</p>
          <a href="mailto:contacto@certy.es" className="mt-8 inline-flex items-center gap-3 text-base font-semibold text-[#07110d] hover:text-[#15783a]">
            <Mail className="h-5 w-5" /> contacto@certy.es
          </a>
        </div>

        <form onSubmit={handleSubmit} className="rounded-xl border border-[#c9d2cc] bg-[#f5f7f5] p-6 md:p-8">
          <div className="grid gap-5 sm:grid-cols-2">
            <label className="text-sm font-semibold text-[#34463b]">Nombre *
              <input type="text" name="name" value={formData.name} onChange={handleChange} required className={`${inputClass} mt-2`} placeholder="Tu nombre" />
            </label>
            <label className="text-sm font-semibold text-[#34463b]">Correo *
              <input type="email" name="email" value={formData.email} onChange={handleChange} required className={`${inputClass} mt-2`} placeholder="nombre@empresa.es" />
            </label>
          </div>
          <label className="mt-5 block text-sm font-semibold text-[#34463b]">Empresa
            <input type="text" name="company" value={formData.company} onChange={handleChange} className={`${inputClass} mt-2`} placeholder="Opcional" />
          </label>
          <label className="mt-5 block text-sm font-semibold text-[#34463b]">Consulta *
            <textarea name="message" value={formData.message} onChange={handleChange} required rows={5} className={`${inputClass} mt-2 resize-none`} placeholder="Describe tu consulta" />
          </label>
          <button type="submit" disabled={isSubmitting} className="editorial-button editorial-button-outline-dark mt-6 w-full justify-center disabled:cursor-not-allowed disabled:opacity-60">
            <Send className="h-4 w-4" /> {isSubmitting ? 'Enviando…' : 'Enviar consulta'}
          </button>

          {submitStatus === 'success' && (
            <p className="mt-5 flex items-center gap-2 text-sm font-medium text-[#15783a]"><CheckCircle className="h-4 w-4" /> Mensaje enviado. Te responderemos por correo.</p>
          )}
          {submitStatus === 'error' && (
            <p className="mt-5 flex items-center gap-2 text-sm font-medium text-red-700"><AlertCircle className="h-4 w-4" /> {errorMessage}</p>
          )}
          <p className="mt-5 text-xs leading-5 text-[#6b7b71]">Al enviar el formulario aceptas nuestra <a href="/privacidad" className="underline underline-offset-2">política de privacidad</a>.</p>
        </form>
      </div>
    </section>
  );
};

export default Contact;
